import React from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { Checkbox, FormControlLabel } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import * as Sentry from "@sentry/nextjs";
import Image from "next/image";
import { signIn } from "next-auth/react";
import { useForm } from "react-hook-form";
import * as Yup from "yup";

import logo from "assets/images/metworx-logo.svg";
import { EMAIL_API_ENDPOINT } from "src/lib/endpoint";

const Container = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  position: "fixed",
  left: 0,
  bottom: 0,
  width: "100%",
  height: "100%",
}));

const Content = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
}));

const Row = styled("div")(() => ({
  width: "100%",
  display: "flex",
  justifyContent: "center",
}));

const EmailInfoContainer = styled("div")(() => ({
  width: "350px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
}));

type FormInput = {
  email: string;
  rememberMe: boolean;
};

const EmailDialog = ({ open, handleClose }) => {
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          Please contact your organization&apos;s Metworx administrator for help verifying your account ID.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} autoFocus>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const EmailForm: React.FC = () => {
  const [error, setError] = React.useState<string>("");
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const orgLookup = async (email: string, rememberMe: boolean): Promise<string | null> => {
    try {
      const resp = await fetch(`${EMAIL_API_ENDPOINT}?email=${email}&remember_me=${rememberMe}`);

      if (!resp.ok) {
        setError("Unable to find your organization");
        return null;
      }

      const body = await resp.json();
      const org = body?.domain;

      if (!org) {
        setError("Organization not found in the response");
        return null;
      }

      Sentry.setTag("org", org);
      return org;
    } catch (error) {
      Sentry.captureException(error);
      setError("An error occurred while looking up the organization");
      return null;
    }
  };

  const onSubmit = async (data: FormInput, e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const org = await orgLookup(data.email, data.rememberMe);
    if (org) {
      try {
        const resp = await signIn(org, { callbackUrl: "/" });
        if (resp?.error) {
          Sentry.captureException(new Error(resp.error));
        }
      } catch (error) {
        Sentry.captureException(error);
        setError("An error occurred during sign in");
      }
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInput>({
    resolver: yupResolver(
      Yup.object()
        .shape({
          email: Yup.string().email("Entered value does not match email format").required("Email is required"),
        })
        .required()
    ),
  });

  return (
    <Container>
      <Box component="form" autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <Content>
          <div>
            <Image src={logo} alt="logo" />
          </div>
          <Row sx={{ mt: 5, mb: 5, color: "red" }}>{error}</Row>
          <EmailInfoContainer>
            <Row>
              <TextField
                id="email"
                name="email"
                type="email"
                placeholder="Email"
                variant="outlined"
                color="primary"
                sx={{ width: "100%" }}
                error={!!errors.email}
                helperText={errors.email?.message}
                {...register("email")}
              />
            </Row>
            <Row sx={{ justifyContent: "space-between", alignItems: "center" }}>
              <FormControlLabel control={<Checkbox {...register("rememberMe")} />} label="Remember me" />
              <Typography
                variant="body2"
                sx={{ textAlign: "center", color: "blue", cursor: "pointer" }}
                onClick={handleClickOpen}
              >
                {"Don't remember email?"}
              </Typography>
              <EmailDialog open={open} handleClose={handleClose} />
            </Row>
            <Button type="submit" size="large" variant="contained" color="primary" sx={{ width: "75%", mt: 10 }}>
              Continue
            </Button>
          </EmailInfoContainer>
        </Content>
      </Box>
    </Container>
  );
};

export default EmailForm;
